import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getMessage } from '../utils/storage';
import { format } from 'date-fns';

const { FiHeart, FiShare2, FiCopy, FiTwitter, FiInstagram, FiFacebook, FiMessageCircle, FiUser, FiClock, FiArrowLeft } = FiIcons;

const MessagePage = () => {
  const { id } = useParams();
  const [message, setMessage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const messageData = await getMessage(id);
        setMessage(messageData);
      } catch (error) {
        console.error("Error fetching message:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessage();
  }, [id]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareText = message ? encodeURIComponent(`Someone shared a kind message with you: "${message.content}"`) : '';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    whatsapp: `https://wa.me/?text=${shareText}%20${shareUrl}`,
    instagram: `https://www.instagram.com/`
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="clay-card text-center">
          <div className="animate-pulse">
            <div className="clay-button-pink p-6 mx-auto mb-6 w-fit">
              <SafeIcon icon={FiHeart} className="text-4xl heart-logo" />
            </div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="clay-card text-center"
        >
          <div className="clay-button-pink p-6 mx-auto mb-6 w-fit">
            <SafeIcon icon={FiHeart} className="text-4xl text-text/40" />
          </div>
          <h2 className="text-3xl font-bold text-heading mb-4">Message Not Found</h2>
          <p className="text-text font-medium mb-8">
            This kindness message doesn't exist or may have been removed.
          </p>
          <Link
            to="/"
            className="clay-button-green px-8 py-4 font-bold text-text hover:transform hover:scale-105 transition-all duration-300 inline-flex items-center space-x-3 focus-visible"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-text" />
            <span className="text-lg">Send Your Own Kindness</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <Link
          to="/"
          className="inline-flex items-center space-x-3 text-text hover:transform hover:scale-105 transition-all duration-300 mb-8 clay-button-yellow px-4 py-2 rounded-full font-bold focus-visible"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg text-text" />
          <span>Back to Home</span>
        </Link>
        
        <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
          A Message of Kindness
        </h1>
        <h2 className="text-2xl font-bold mb-4 kaas-subtitle">
          KAAS – Kindness as a Service
        </h2>
        <p className="text-text font-medium text-lg">Someone wanted to brighten your day</p>
      </motion.div>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="message-card mb-8"
      >
        <div className="message-card-header">
          <SafeIcon icon={FiHeart} className="text-3xl heart-logo" />
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiClock} className="text-lg text-text" />
            <span className="font-bold text-text">
              {format(new Date(message.timestamp), 'MMMM d, yyyy')}
            </span>
          </div>
        </div>
        <div className="p-8 bg-card-bg">
          {message.title && (
            <h3 className="text-2xl font-bold text-heading mb-4">{message.title}</h3>
          )}
          <blockquote className="text-2xl md:text-3xl text-text leading-relaxed font-medium">
            "{message.content}"
          </blockquote>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-text/20 message-card-footer">
          <div className="flex items-center space-x-3 text-text">
            <SafeIcon icon={FiUser} className="text-lg text-text" />
            <span className="font-bold">
              {message.isAnonymous ? 'Anonymous Kindness' : message.sender}
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="clay-card"
      >
        <div className="flex items-center space-x-4 mb-6">
          <div className="clay-button-purple p-3">
            <SafeIcon icon={FiShare2} className="text-xl text-text" />
          </div>
          <h3 className="text-2xl font-bold text-heading">Share This Kindness</h3>
        </div>

        <p className="text-text font-medium mb-8">
          Help spread positivity by sharing this message with others who might need it.
        </p>

        <div className="space-y-6">
          <button
            onClick={handleCopyLink}
            className={`clay-button-green w-full py-4 flex items-center justify-center space-x-3 font-bold transition-all duration-300 focus-visible ${
              copied ? 'transform scale-105' : 'hover:transform hover:scale-105'
            }`}
          >
            <SafeIcon icon={FiCopy} className="text-xl text-text" />
            <span className="text-lg">{copied ? 'Link Copied!' : 'Copy Link'}</span>
          </button>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="clay-button-yellow p-4 flex items-center justify-center space-x-2 hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <SafeIcon icon={FiTwitter} className="text-xl text-text" />
              <span className="font-bold hidden md:block">Twitter</span>
            </a>

            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="clay-button-pink p-4 flex items-center justify-center space-x-2 hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <SafeIcon icon={FiFacebook} className="text-xl text-text" />
              <span className="font-bold hidden md:block">Facebook</span>
            </a>

            <a
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="clay-button-green p-4 flex items-center justify-center space-x-2 hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <SafeIcon icon={FiMessageCircle} className="text-xl text-text" />
              <span className="font-bold hidden md:block">WhatsApp</span>
            </a>

            <button
              onClick={() => {
                alert('Instagram doesn\'t support direct URL sharing. Please copy the link and share it in your Instagram story or post!');
              }}
              className="clay-button-purple p-4 flex items-center justify-center space-x-2 hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <SafeIcon icon={FiInstagram} className="text-xl text-text" />
              <span className="font-bold hidden md:block">Instagram</span>
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="text-center mt-8"
      >
        <Link
          to="/"
          className="clay-button-pink px-10 py-5 inline-flex items-center space-x-3 font-bold text-text hover:transform hover:scale-105 transition-all duration-300 focus-visible"
        >
          <SafeIcon icon={FiHeart} className="text-2xl heart-logo" />
          <span className="text-xl">Send Your Own Kindness</span>
        </Link>
      </motion.div>
    </div>
  );
};

export default MessagePage;